"use client";
import React from "react";
import { Caveat } from "next/font/google";
import Image from "next/image";
import { motion } from "framer-motion";

const caveat = Caveat({ subsets: ["latin"], weight: ["400", "700"], display: "swap" });

export default function FitnessMaintenanceSection({ section }) {
  const title = section?.title || "Next-level organization";
  const mainText = section?.mainText || "<strong className='text-white font-semibold'>Equipment maintenance.</strong> Use Odoo's Maintenance app to make sure that all your equipment is in top-notch condition at all times.";
  const maintenanceImage = section?.maintenanceImage || "/odoo/industries/fitness/equipment-maintenance.png";

  return (
    <section className="py-20 lg:py-24 px-6 sm:px-12 lg:px-24 relative overflow-visible bg-transparent font-sans text-white w-full z-10">
      <div className="container mx-auto max-w-[1400px]">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* Left Column: Heading & Text Description */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 space-y-8"
          >
            {/* Heading */}
            <h2 className={`text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-tight ${caveat.className}`}>
              <span>Next-level </span>
              <span className="relative inline-block pb-3 text-white">
                organization
                <svg 
                  className="absolute bottom-0 left-0 w-full h-3.5 text-rose-400 overflow-visible" 
                  viewBox="0 0 100 20" 
                  fill="none" 
                  preserveAspectRatio="none"
                >
                  <path 
                    d="M 5 12 Q 50 18, 95 12" 
                    stroke="currentColor" 
                    strokeWidth="6" 
                    strokeDasharray="6 6"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h2>

            {/* Subtext */}
            <p 
              className="text-slate-300 text-lg md:text-xl leading-relaxed font-normal pt-2 max-w-xl"
              dangerouslySetInnerHTML={{ __html: mainText }}
            />
          </motion.div>

          {/* Right Column: Maintenance Kanban Card with Soft Wavy Light Backdrop */}
          <motion.div 
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-6 relative flex justify-center items-center"
          >
            {/* Soft Wavy Backdrop Shape */}
            <div 
              className="absolute -top-8 -left-8 w-[108%] h-[116%] bg-white/5 -z-10 shadow-2xl pointer-events-none"
              style={{
                borderTopLeftRadius: "280px",
                borderBottomLeftRadius: "280px",
                borderTopRightRadius: "60px",
                borderBottomRightRadius: "60px",
              }}
            />

            {/* Maintenance Kanban Card Image */}
            <div className="relative w-full rounded-2xl overflow-hidden shadow-2xl border border-white/10 bg-white transition-transform duration-300 hover:scale-[1.01]">
              <Image
                src={maintenanceImage}
                alt="Equipment maintenance interface"
                width={700}
                height={460}
                className="w-full h-auto object-cover rounded-2xl"
                priority
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}
